import { useState } from 'react'
import { notifySuccess } from '@/lib/notify'
import { cn } from '@/lib/cn'

export const MASKED_KEY = '••••-••••-••••-••••'

interface KeyStripProps {
  /** Null while the key is unavailable; the strip still shows its masked state. */
  value: string | null
  /** Names the key in the "copied" message. */
  title: string
}

/**
 * A won key, covered until asked for: the first click reveals it, the next
 * copies it.
 */
export function KeyStrip({ value, title }: KeyStripProps) {
  const [revealed, setRevealed] = useState(false)

  const onClick = async () => {
    if (!value) return
    if (!revealed) {
      setRevealed(true)
      return
    }
    try {
      await navigator.clipboard.writeText(value)
      notifySuccess(`Copied the ${title} key`)
    } catch {
      // Clipboard permission denied — the key is on screen and selectable.
    }
  }

  return (
    <button
      type="button"
      className={cn('key-strip', revealed && 'revealed')}
      onClick={onClick}
      disabled={!value}
      title={revealed ? 'Click to copy' : 'Click to reveal'}
    >
      <span className="key-strip-value">{revealed && value ? value : MASKED_KEY}</span>
      <span className="key-strip-hint">{revealed ? 'Copy' : 'Reveal'}</span>
    </button>
  )
}
